import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, RotateCcw, MessageSquare, CheckCircle2, XCircle, Clock, X, Zap, Plus } from "lucide-react";
import { adminAuthHeader } from "@/hooks/useAdminAuth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (iso) => { try { return new Date(iso).toLocaleString("it-IT"); } catch { return iso || "—"; } };

const STATUS_STYLE = {
  pending:   { label: "In attesa",   color: "#f59e0b", bg: "#fef3c7", icon: Clock },
  sent:      { label: "Inviato",     color: "#16a34a", bg: "#dcfce7", icon: CheckCircle2 },
  skipped:   { label: "Saltato",     color: "#dc2626", bg: "#fee2e2", icon: XCircle },
  failed:    { label: "Fallito",     color: "#dc2626", bg: "#fee2e2", icon: XCircle },
  cancelled: { label: "Annullato",   color: "#64748b", bg: "#f1f5f9", icon: X },
  running:   { label: "In corso",    color: "#3b82f6", bg: "#dbeafe", icon: Loader2 },
};

// Human-friendly rule descriptions — one card per sequence needs to explain
// WHEN it fires and WHAT it says, so the admin doesn't have to remember the
// rule matrix. Keep in sync with backend `templates_db.RULE_DEFAULTS`.
const RULE_HUMAN = {
  R1:  "Lead registrato ma NON ha messo la password (+2 min)",
  R2:  "Password OK, L.1 non avviata, primo msg (+5 min)",
  R3:  "Password OK, L.1 non avviata, ha già ricevuto un msg (+5 min)",
  R4:  "L.1 avviata ma <80%, primo msg (+15 min)",
  R5:  "L.1 avviata ma <80%, aveva ricevuto msg password/L.1 (+15 min)",
  R6:  "L.1 avviata ma <80%, aveva ricevuto altro tipo di msg (+15 min)",
  R7:  "L.1 al 80% ma non ha chiesto L.2, primo msg (+5 min)",
  R8:  "L.1 al 80% ma non ha chiesto L.2, aveva ricevuto feedback (+5 min)",
  R9:  "L.1 al 80% ma non ha chiesto L.2, aveva ricevuto password/L.1 (+5 min)",
  R10: "Ha chiesto sblocco L.2, aveva già ricevuto msg (+2 min)",
  R11: "Ha chiesto sblocco L.2, non aveva mai ricevuto msg (+2 min)",
};

/**
 * Timeline of WhatsApp messages scheduled/sent/skipped/failed for a single user.
 * For each row that is NOT `sent`, the admin can click "Reinvia" to reset it
 * back to `pending` with `send_at = now`. The backend endpoint also cascades
 * the retry to later sub-messages of the same sequence, preserving their
 * relative delays (see /admin/scheduled-messages/{id}/retry).
 */
export default function UserScheduledMessagesPanel({ userId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [retrying, setRetrying] = useState(null);
  const [scheduling, setScheduling] = useState(false);
  // Default the manual-trigger dropdown to R2 (the user's explicit ask:
  // "chi non ha avviato L.1") — most common case for legacy leads that
  // missed the automated engine window.
  const [pickedRule, setPickedRule] = useState("R2");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/users/${userId}/scheduled-messages`,
        { headers: adminAuthHeader() });
      setItems(data?.items || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore caricamento messaggi");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { if (userId) load(); }, [userId]);

  // Rules the user ALREADY has scheduled — those are hidden from the manual
  // dropdown so the admin doesn't accidentally re-trigger (backend would
  // reject them as idempotent anyway, but this makes the UI cleaner).
  const scheduledRuleCodes = useMemo(() => {
    const s = new Set();
    items.forEach((m) => { if (m.rule_code) s.add(m.rule_code); });
    return s;
  }, [items]);

  const scheduleManually = async () => {
    if (!pickedRule || scheduling) return;
    const humanDesc = RULE_HUMAN[pickedRule] || pickedRule;
    if (!window.confirm(
      `Programmare adesso la sequenza ${pickedRule}?\n\n` +
      `Regola: ${humanDesc}\n\n` +
      "Usata soprattutto per lead 'vecchi' che non hanno mai ricevuto questa sequenza in automatico. " +
      "I delay tra i sotto-messaggi verranno applicati partendo da adesso."
    )) return;
    setScheduling(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/users/${userId}/schedule-rule`,
        { rule_code: pickedRule },
        { headers: adminAuthHeader() },
      );
      if (data?.already_had_sequence) {
        toast.info(`Sequenza ${pickedRule} già presente per questo utente (nessun duplicato creato)`);
      } else if ((data?.scheduled || 0) === 0) {
        toast.warning(`Sequenza ${pickedRule} non programmata (regola disabilitata globalmente?)`);
      } else {
        toast.success(`${data.scheduled} messaggi programmati per la sequenza ${pickedRule}`);
      }
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore programmazione sequenza");
    } finally {
      setScheduling(false);
    }
  };

  const retry = async (msg) => {
    if (msg.status === "sent") return;
    if (!window.confirm(`Reinviare il messaggio ${msg.template_tag}?`)) return;
    setRetrying(msg.id);
    try {
      const { data } = await axios.post(`${API}/admin/scheduled-messages/${msg.id}/retry`,
        {}, { headers: adminAuthHeader() });
      const cascaded = data?.cascaded || 0;
      toast.success(cascaded
        ? `Reinviato + ${cascaded} sotto-messaggi successivi ripianificati`
        : "Reinviato");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore retry");
    } finally {
      setRetrying(null);
    }
  };

  const retrySequence = async (seqId, ruleCode) => {
    if (!window.confirm(
      `Reinviare l'INTERA sequenza ${ruleCode || ""}?\n` +
      "Il primo sotto-messaggio parte subito, gli altri seguiranno con i loro delay originali (es. +10s, +25s, +29s)."
    )) return;
    setRetrying(seqId);
    try {
      const { data } = await axios.post(`${API}/admin/scheduled-messages/sequence/${seqId}/retry`,
        {}, { headers: adminAuthHeader() });
      toast.success(`Sequenza rimandata: ${data?.sub_messages || 0} sotto-messaggi ripianificati`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore retry sequenza");
    } finally {
      setRetrying(null);
    }
  };

  if (loading) return (
    <div className="rounded-2xl p-6 border bg-white flex items-center justify-center" style={{ borderColor: "#dbe3ec" }}>
      <Loader2 className="animate-spin" size={18} />
    </div>
  );

  // Reusable manual-schedule bar — shown both in the empty state (for old
  // leads with zero scheduled msgs) AND at the top of the panel when the
  // user already has some sequences (admin might want to add another one
  // that wasn't triggered automatically).
  const ruleOptions = Object.keys(RULE_HUMAN)
    .sort((a, b) => parseInt(a.slice(1), 10) - parseInt(b.slice(1), 10));
  const manualBar = (
    <div
      className="rounded-lg p-3 border mb-4"
      style={{ borderColor: "#fde68a", background: "#fffbeb" }}
      data-testid="manual-schedule-bar"
    >
      <p className="text-[11px] font-semibold uppercase tracking-wider mb-2" style={{ color: "#92400e" }}>
        Programma sequenza manualmente
      </p>
      <div className="flex items-center gap-2 flex-wrap">
        <select
          value={pickedRule}
          onChange={(e) => setPickedRule(e.target.value)}
          data-testid="manual-schedule-rule-select"
          className="flex-1 min-w-[260px] rounded-md border px-2 py-1.5 text-[12px] bg-white"
          style={{ borderColor: "#dbe3ec", color: "var(--np-primary)" }}
        >
          {ruleOptions.map((r) => {
            const already = scheduledRuleCodes.has(r);
            return (
              <option key={r} value={r}>
                {r} — {RULE_HUMAN[r]}{already ? "  •  già programmata" : ""}
              </option>
            );
          })}
        </select>
        <button
          type="button"
          onClick={scheduleManually}
          disabled={scheduling || !pickedRule}
          data-testid="manual-schedule-submit"
          className="inline-flex items-center gap-1.5 text-[12px] font-semibold px-3 py-1.5 rounded-md text-white disabled:opacity-50"
          style={{ background: "#f59e0b" }}
        >
          {scheduling ? <Loader2 className="animate-spin" size={13} /> : <Plus size={13} />}
          Programma ora
        </button>
      </div>
      <p className="text-[10px] mt-2" style={{ color: "#92400e" }}>
        Utile per lead vecchi che non hanno mai ricevuto la sequenza in automatico.
        Il primo msg parte tra pochi secondi, gli altri seguiranno con i delay configurati.
      </p>
    </div>
  );

  if (!items.length) return (
    <div className="rounded-2xl p-6 border bg-white" style={{ borderColor: "#dbe3ec" }} data-testid="scheduled-messages-empty">
      <div className="flex items-center gap-2 mb-3">
        <MessageSquare size={16} style={{ color: "var(--np-secondary)" }} />
        <h3 className="text-[13px] font-semibold uppercase tracking-[0.16em]" style={{ color: "var(--np-primary)" }}>Messaggi WhatsApp</h3>
      </div>
      <p className="text-sm mb-4" style={{ color: "var(--np-muted)" }}>
        Nessun messaggio programmato per questo utente. Se è un lead &quot;vecchio&quot; (registrato prima
        del follow-up engine) puoi programmare manualmente una sequenza qui sotto.
      </p>
      {manualBar}
    </div>
  );

  // Group by sequence_id so the admin sees the sequences as blocks
  const groups = {};
  items.forEach((m) => {
    const k = m.sequence_id || `_single_${m.id}`;
    if (!groups[k]) groups[k] = [];
    groups[k].push(m);
  });
  Object.values(groups).forEach((arr) => arr.sort((a, b) => (a.sequence_index ?? 0) - (b.sequence_index ?? 0)));

  return (
    <section className="rounded-2xl p-6 border bg-white" style={{ borderColor: "#dbe3ec" }} data-testid="user-scheduled-messages">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <MessageSquare size={16} style={{ color: "var(--np-secondary)" }} />
          <h3 className="text-[13px] font-semibold uppercase tracking-[0.16em]" style={{ color: "var(--np-primary)" }}>
            Messaggi WhatsApp ({items.length})
          </h3>
        </div>
        <button
          type="button" onClick={load}
          className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1.5 rounded-md border"
          style={{ borderColor: "#cbd5e1", color: "#475569", background: "#fff" }}
        >
          <RotateCcw size={11} /> Aggiorna
        </button>
      </div>

      {manualBar}

      <div className="space-y-4">
        {Object.entries(groups).map(([grpKey, msgs]) => {
          const rule = msgs[0].rule_code;
          const seqId = msgs[0].sequence_id;
          const canRetrySeq = !!seqId && msgs.length > 1;
          const humanTitle = rule ? RULE_HUMAN[rule] : null;
          const anyNonSent = msgs.some((m) => m.status !== "sent" && m.status !== "pending");
          return (
            <div key={grpKey} className="border rounded-lg overflow-hidden" style={{ borderColor: "#e2e8f0" }}>
              {rule && (
                <div className="px-3 py-2 flex items-center justify-between gap-2 flex-wrap" style={{ background: "#f8fafc" }}>
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <span className="font-bold uppercase tracking-wider text-[11px] px-1.5 py-0.5 rounded" style={{ background: "var(--np-secondary)", color: "#fff" }}>
                      {rule}
                    </span>
                    {msgs[0].variant && (
                      <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full" style={{ background: "#eff6ff", color: "#1e40af" }}>
                        Variante {msgs[0].variant}
                      </span>
                    )}
                    <span className="text-[11px] truncate" style={{ color: "#475569" }}>
                      {humanTitle || `${msgs.length} sotto-msg`}
                    </span>
                  </div>
                  {canRetrySeq && (
                    <button
                      type="button"
                      onClick={() => retrySequence(seqId, rule)}
                      disabled={retrying === seqId}
                      data-testid={`retry-sequence-btn-${seqId}`}
                      title="Reinvia tutta la sequenza rispettando i delay originali"
                      className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-1 rounded-md text-white disabled:opacity-50"
                      style={{ background: anyNonSent ? "#dc2626" : "var(--np-secondary)" }}
                    >
                      {retrying === seqId ? <Loader2 size={10} className="animate-spin" /> : <Zap size={10} />}
                      Reinvia intera sequenza
                    </button>
                  )}
                </div>
              )}
              <div className="divide-y" style={{ borderColor: "#eef2f7" }}>{msgs.map((m) => {
                const s = STATUS_STYLE[m.status] || STATUS_STYLE.skipped;
                const StatusIcon = s.icon;
                return (
                  <div key={m.id} className="px-3 py-2.5 flex items-center gap-3 flex-wrap" data-testid={`sched-msg-row-${m.id}`}>
                    <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full" style={{ background: s.bg, color: s.color }}>
                      <StatusIcon size={10} className={m.status === "running" ? "animate-spin" : ""} /> {s.label}
                    </span>
                    <code className="text-[11px] font-mono px-2 py-0.5 rounded" style={{ background: "#f1f5f9", color: "#475569" }}>
                      {m.template_tag}
                    </code>
                    {m.sequence_index != null && m.sequence_total > 1 && (
                      <span className="text-[10px]" style={{ color: "var(--np-muted)" }}>
                        {m.sequence_index + 1}/{m.sequence_total}
                      </span>
                    )}
                    <span className="text-[11px] flex-1 min-w-[140px]" style={{ color: "var(--np-muted)" }}>
                      {m.status === "sent" ? `inviato: ${fmt(m.sent_at)}` : `programmato: ${fmt(m.send_at)}`}
                    </span>
                    {m.status !== "sent" && m.status !== "pending" && m.status !== "running" && (
                      <button
                        type="button"
                        onClick={() => retry(m)}
                        disabled={retrying === m.id}
                        data-testid={`retry-msg-btn-${m.id}`}
                        className="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-md text-white disabled:opacity-50"
                        style={{ background: "var(--np-secondary)" }}
                      >
                        {retrying === m.id ? <Loader2 size={11} className="animate-spin" /> : <RotateCcw size={11} />}
                        Reinvia
                      </button>
                    )}
                    {m.status === "pending" && new Date(m.send_at) > new Date() && (
                      <button
                        type="button"
                        onClick={() => retry(m)}
                        disabled={retrying === m.id}
                        title="Invia subito (bypassa il delay)"
                        data-testid={`send-now-btn-${m.id}`}
                        className="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-md border"
                        style={{ borderColor: "var(--np-secondary)", color: "var(--np-secondary)", background: "#fff" }}
                      >
                        {retrying === m.id ? <Loader2 size={11} className="animate-spin" /> : <RotateCcw size={11} />}
                        Invia subito
                      </button>
                    )}
                    {m.provider_info && (
                      <span className="text-[10px] font-mono w-full pl-2" style={{ color: "#dc2626" }}>
                        {m.provider_info}{m.skip_reason ? ` · ${m.skip_reason}` : ""}
                      </span>
                    )}
                    {m.retry_count > 0 && (
                      <span className="text-[10px]" style={{ color: "var(--np-muted)" }}>
                        retry: {m.retry_count}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
          );
        })}
      </div>
    </section>
  );
}
